import { useEffect, useState } from "react";
import { BASE_URL, CROP_LIST, DEFAULT_DATA_ITEM, DEFAULT_DATA_ITEM_ARRAY, TypeDataItem, TypeDataSource, TypeValueChain } from "../../helpers/constants";
import useApiRequest from "../../helpers/api_request";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectAccessToken } from "../../features/auth/authSlice";
import MultipleInput from "../elements/MultipleInput";
import AppButton from "../elements/AppButton";
import { useNavigate, useParams } from "react-router-dom";
import { showToastMessage } from "../../features/generalSlice";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import useProductionData from "../../hooks/useProductionData";
import AppSpinner from "../elements/AppSpinner";


export type TypeProductionData = {
    "_id"?: string,
    "value_chain_id"?: string,
    "year": string,
    "no_of_seed_companies": TypeDataItem,
    "no_of_farmers": TypeDataItem,
    "no_of_farmer_groups": TypeDataItem,
    "no_of_members_in_groups": TypeDataItem,
    "acreage": TypeDataItem,
    "avg_fertilizer_produced": TypeDataItem,
    "avg_fertilizer_used": TypeDataItem,
    "no_of_farmers_using_organic_fertilizer": TypeDataItem,
    "no_of_farmers_using_machines": TypeDataItem,
    "no_of_crop_varieties_per_season": TypeDataItem,
    "yield_per_acre": TypeDataItem,
    "no_of_maize_season_per_year": TypeDataItem,
    "list_of_pests_and_diseases": TypeDataItem,
    "no_of_certified_nursery_operators": TypeDataItem,
    "no_of_seedling_distributes_per_season": TypeDataItem,
    "no_of_extension_officers": TypeDataItem,
    "no_of_certified_ware_houses": TypeDataItem,
    "no_of_women_involved_in_value_chain": TypeDataItem,
    "farm_gate_price_for_arabica": TypeDataItem,
    "farm_gate_price_for_robusta": TypeDataItem,
    "created_at"?: "string",
    "value_chain"?: string
}

const CreateProductionPage = () => {
    const [dataSources, setDataSources] = useState<TypeDataSource[] | []>([]);
    const apiRequest = useApiRequest();
    const accessToken = useAppSelector(selectAccessToken);
    const [valueChains, setValueChains] = useState<TypeValueChain[] | []>([]);
    const [isSavingData, setIsSavingData] = useState(false);
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [productionData, setProductionData] = useState<TypeProductionData>({
        "value_chain_id": "",
        "value_chain": "",
        "year": "",
        "no_of_seed_companies": DEFAULT_DATA_ITEM,
        "no_of_farmers": DEFAULT_DATA_ITEM,
        "no_of_farmer_groups": DEFAULT_DATA_ITEM,
        "no_of_members_in_groups": DEFAULT_DATA_ITEM,
        "acreage": DEFAULT_DATA_ITEM,
        "avg_fertilizer_produced": DEFAULT_DATA_ITEM,
        "avg_fertilizer_used": DEFAULT_DATA_ITEM,
        "no_of_farmers_using_organic_fertilizer": DEFAULT_DATA_ITEM,
        "no_of_farmers_using_machines": DEFAULT_DATA_ITEM,
        "no_of_crop_varieties_per_season": DEFAULT_DATA_ITEM,
        "yield_per_acre": DEFAULT_DATA_ITEM,
        "no_of_maize_season_per_year": DEFAULT_DATA_ITEM,
        "list_of_pests_and_diseases": DEFAULT_DATA_ITEM_ARRAY,
        "no_of_certified_nursery_operators": DEFAULT_DATA_ITEM,
        "no_of_seedling_distributes_per_season": DEFAULT_DATA_ITEM,
        "no_of_extension_officers": DEFAULT_DATA_ITEM,
        "no_of_certified_ware_houses": DEFAULT_DATA_ITEM,
        "no_of_women_involved_in_value_chain": DEFAULT_DATA_ITEM,
        "farm_gate_price_for_arabica": DEFAULT_DATA_ITEM,
        "farm_gate_price_for_robusta": DEFAULT_DATA_ITEM
    });
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

    const { loadingData, getProductionData, productionData: productionDataList } = useProductionData();
    const { id } = useParams();

    useEffect(() => {
        if (id != undefined) {
            getProductionData();
        }
    }, []);

    useEffect(() => {
        if (id != undefined) {
            if (productionDataList.length > 0) {
                const record = productionDataList.filter((item) => item._id == id)[0]
                for (const key in record) {
                    const field = record[key as keyof TypeProductionData]
                    setProductionData((prevData) => ({
                        ...prevData,
                        [key]: field,
                    }));
                }
                addToDataSources(record)
            }
        }
    }, [productionDataList])

    const addToDataSources = (record: TypeProductionData) => {
        const otherDataSources: TypeDataSource[] = []
        for (const key in record) {
            const field = record[key as keyof TypeProductionData]
            if (typeof field == "object") {
                if (field) {
                    const dataSource = {
                        id: null,
                        name: field.data_source
                    }
                    otherDataSources.push(dataSource)
                }
            }
        }
        const uniqueValues = otherDataSources.filter(
            (item, index, self) =>
                index === self.findIndex((i) => i.name === item.name)
        );
        setDataSources((prev) => [...prev, ...uniqueValues])
    }

    useEffect(() => {
        getDataSources()
        getValueChains()
    }, [])

    // useEffect(() => {
    //     console.log(productionData)
    // }, [productionData])

    const saveProductionData = async () => {
        if (productionData.value_chain === "") {
            dispatch(showToastMessage({
                message: "Value chain is required",
                type: "error",
                position: toast.POSITION.TOP_RIGHT
            }));
            return
        }

        setIsSavingData(true)
        await apiRequest({
            method: 'post',
            url: BASE_URL + "/api/production/create",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            data: productionData
        }).then((response) => {
            setIsSavingData(false)
            dispatch(showToastMessage({
                message: "Production data Saved",
                type: "success",
                position: toast.POSITION.TOP_RIGHT
            }));
            navigate("/production")
        }).catch((error) => {
            const errorResponse = error as AxiosError;
            if (errorResponse.response) {
                const data = errorResponse.response.data as { message: string, status: string };
                dispatch(showToastMessage({
                    message: data.message,
                    type: "error",
                    position: toast.POSITION.TOP_RIGHT
                }));
            } else {
                dispatch(showToastMessage({
                    message: "Something went wrong",
                    type: "error",
                    position: toast.POSITION.TOP_RIGHT
                }));
            }
            setIsSavingData(false)
        });
    }

    const getValueChains = async () => {
        await apiRequest({
            method: 'get',
            url: BASE_URL + "/api/value-chain",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }).then((response) => {
            setValueChains(response.data)
        }).catch((error) => {

        });
    }

    const getDataSources = async () => {
        await apiRequest({
            method: 'get',
            url: BASE_URL + "/api/data-source",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }).then((response) => {
            setDataSources((prev) => [...prev, ...response.data])
        }).catch((error) => {

        });
    }

    const setDataItemValue = (field: keyof TypeProductionData, value: string | string[]) => {
        setProductionData({
            ...productionData,
            [field]: {
                ...productionData[field] as TypeDataItem, value: value
            }
        });
    }

    // const getDataItemValue = (field: keyof TypeProductionData) => {
    //     setProductionData({
    //         ...productionData,
    //         [field]: {
    //             ...productionData[field] as TypeDataItem, value: value
    //         }
    //     });
    // }

    const setDataItemSource = (field: keyof TypeProductionData, data_source: string | string[]) => {
        setProductionData({
            ...productionData,
            [field]: {
                ...productionData[field] as TypeDataItem, data_source: data_source
            }
        });
    }

    return (
        <div>
            <div className="card">
                <div className=" card-body min-h-half-screen">
                    <div className="flex">
                        <nav className="flex justify-between " aria-label="Breadcrumb">
                            <ol className="inline-flex items-center mb-3 sm:mb-0">
                                <li>
                                    <div className="flex items-center">
                                        <h5 className=" card-title ">Production</h5>
                                    </div>
                                </li>
                                <span className="mx-2 text-gray-400">/</span>
                                <li aria-current="page">
                                    <div className="flex items-center">
                                        <h5 className=" card-title ">{id ? "Edit" : "Create"}</h5>
                                    </div>
                                </li>
                            </ol>
                        </nav>
                    </div>
                    {(loadingData) && <AppSpinner TopClass=" flex pt-0" />}

                    <div className=" h-fit flex-grow flex flex-col justify-center ">
                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">Value Chain</label>
                            <select value={productionData.value_chain} onChange={(e) =>
                                setProductionData({ ...productionData, value_chain: e.target.value })} className="form-input-2" name="" id="">
                                <option value="">--Select--</option>
                                {valueChains.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                            </select>
                        </div>

                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">Year</label>
                            <select value={productionData.year} onChange={(e) => setProductionData({ ...productionData, year: e.target.value })} className="form-input-2" name="" id="">
                                <option value="">Select</option>
                                {years.map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">No. of Seed Companies</label>
                            <div className="grid grid-cols-2 gap-2">
                                <input value={productionData.no_of_seed_companies.value} onChange={(e) => setDataItemValue("no_of_seed_companies", e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <select value={productionData.no_of_seed_companies.data_source} onChange={(e) => setDataItemSource('no_of_seed_companies', e.target.value)} className="form-input-2" name="" id="">
                                    <option value="">--Select Source--</option>
                                    {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 "> No. of Farmers</label>
                            <div className="grid grid-cols-2 gap-2">
                                <input value={productionData.no_of_farmers.value} onChange={(e) => setDataItemValue('no_of_farmers', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <select value={productionData.no_of_farmers.data_source} onChange={(e) => setDataItemSource('no_of_farmers', e.target.value)} className="form-input-2" name="" id="">
                                    <option value="">--Select Source--</option>
                                    {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className=" w-2/3 mt-4 ">
                            <label className="block text-sm font-medium text-gray-700 "> No of farmer groups</label>

                            <div className="grid grid-cols-2 gap-2">
                                <input value={productionData.no_of_farmer_groups.value} onChange={(e) => setDataItemValue('no_of_farmer_groups', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <select value={productionData.no_of_farmer_groups.data_source} onChange={(e) => setDataItemSource('no_of_farmer_groups', e.target.value)} className="form-input-2" name="" id="">
                                    <option value="">--Select Source--</option>
                                    {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 "> No of members in cooperatives/groups</label>

                            <div className="grid grid-cols-2 gap-2">
                                <input value={productionData.no_of_members_in_groups.value} onChange={(e) => setDataItemValue("no_of_members_in_groups", e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <select value={productionData.no_of_members_in_groups.data_source} onChange={(e) => setDataItemSource('no_of_members_in_groups', e.target.value)} className="form-input-2" name="" id="">
                                    <option value="">--Select Source--</option>
                                    {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">Acreage (Hect)</label>

                            <div className="grid grid-cols-2 gap-2">
                                <input value={productionData.acreage.value} onChange={(e) => setDataItemValue("acreage", e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <select value={productionData.acreage.data_source} onChange={(e) => setDataItemSource('acreage', e.target.value)} className="form-input-2" name="" id="">
                                    <option value="">--Select Source--</option>
                                    {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">Avg Fertilizer produced (Kg)</label>

                            <div className="grid grid-cols-2 gap-2">
                                <input value={productionData.avg_fertilizer_produced.value} onChange={(e) => setDataItemValue('avg_fertilizer_produced', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <select value={productionData.avg_fertilizer_produced.data_source} onChange={(e) => setDataItemSource('avg_fertilizer_produced', e.target.value)} className="form-input-2" name="" id="">
                                    <option value="">--Select Source--</option>
                                    {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">Avg Fertilizer used (Kg)</label>
                            <div className="grid grid-cols-2 gap-2">
                                <input value={productionData.avg_fertilizer_used.value} onChange={(e) => setDataItemValue('avg_fertilizer_used', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <select value={productionData.avg_fertilizer_used.data_source} onChange={(e) => setDataItemSource('avg_fertilizer_used', e.target.value)} className="form-input-2" name="" id="">
                                    <option value="">--Select Source--</option>
                                    {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">No of farmer using organic fertilizer</label>
                            <div className="grid grid-cols-2 gap-2">
                                <input value={productionData.no_of_farmers_using_organic_fertilizer.value} onChange={(e) => setDataItemValue('no_of_farmers_using_organic_fertilizer', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <select value={productionData.no_of_farmers_using_organic_fertilizer.data_source} onChange={(e) => setDataItemSource('no_of_farmers_using_organic_fertilizer', e.target.value)} className="form-input-2" name="" id="">
                                    <option value="">--Select Source--</option>
                                    {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">No of Farmers using Machines.</label>

                            <div className="grid grid-cols-2 gap-2">
                                <input value={productionData.no_of_farmers_using_machines.value} onChange={(e) => setDataItemValue('no_of_farmers_using_machines', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <select value={productionData.no_of_farmers_using_machines.data_source} onChange={(e) => setDataItemSource('no_of_farmers_using_machines', e.target.value)} className="form-input-2" name="" id="">
                                    <option value="">--Select Source--</option>
                                    {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">No of crop varieties per season.</label>
                            <div className="grid grid-cols-2 gap-2">
                                <input value={productionData.no_of_crop_varieties_per_season.value} onChange={(e) => setDataItemValue('no_of_crop_varieties_per_season', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <select value={productionData.no_of_crop_varieties_per_season.data_source} onChange={(e) => setDataItemSource('no_of_crop_varieties_per_season', e.target.value)} className="form-input-2" name="" id="">
                                    <option value="">--Select Source--</option>
                                    {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">Yield per acre (kg).</label>
                            <div className="grid grid-cols-2 gap-2">
                                <input value={productionData.yield_per_acre.value} onChange={(e) => setDataItemValue('yield_per_acre', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <select value={productionData.yield_per_acre.data_source} onChange={(e) => setDataItemSource('yield_per_acre', e.target.value)} className="form-input-2" name="" id="">
                                    <option value="">--Select Source--</option>
                                    {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">No of seasons per year (Optional).</label>

                            <div className="grid grid-cols-2 gap-2">
                                <input value={productionData.no_of_maize_season_per_year.value} onChange={(e) => setDataItemValue('no_of_maize_season_per_year', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <select value={productionData.no_of_maize_season_per_year.data_source} onChange={(e) => setDataItemSource('no_of_maize_season_per_year', e.target.value)} className="form-input-2" name="" id="">
                                    <option value="">--Select Source--</option>
                                    {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">List of pests & Diseases (Optional).</label>
                            <div className="grid grid-cols-2 gap-2">
                                <MultipleInput inputValues={productionData.list_of_pests_and_diseases.value as string[]} getValues={(values) => setDataItemValue('list_of_pests_and_diseases', values)} />
                                <div>
                                    <select value={productionData.list_of_pests_and_diseases.data_source} onChange={(e) => setDataItemSource('list_of_pests_and_diseases', e.target.value)} className="form-input-2" name="" id="">
                                        <option value="">--Select Source--</option>
                                        {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">No of certified nursery operators (Optional).</label>

                            <div className="grid grid-cols-2 gap-2">
                                <input value={productionData.no_of_certified_nursery_operators.value} onChange={(e) => setDataItemValue('no_of_certified_nursery_operators', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <select value={productionData.no_of_certified_nursery_operators.data_source} onChange={(e) => setDataItemSource('no_of_certified_nursery_operators', e.target.value)} className="form-input-2" name="" id="">
                                    <option value="">--Select Source--</option>
                                    {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">No of seedling distributes per season (Optional).</label>
                            <div className="grid grid-cols-2 gap-2">
                                <input value={productionData.no_of_seedling_distributes_per_season.value} onChange={(e) => setDataItemValue('no_of_seedling_distributes_per_season', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <select value={productionData.no_of_seedling_distributes_per_season.data_source} onChange={(e) => setDataItemSource('no_of_seedling_distributes_per_season', e.target.value)} className="form-input-2" name="" id="">
                                    <option value="">--Select Source--</option>
                                    {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">No of Extension officers</label>

                            <div className="grid grid-cols-2 gap-2">
                                <input value={productionData.no_of_extension_officers.value} onChange={(e) => setDataItemValue('no_of_extension_officers', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <select value={productionData.no_of_extension_officers.data_source} onChange={(e) => setDataItemSource('no_of_extension_officers', e.target.value)} className="form-input-2" name="" id="">
                                    <option value="">--Select Source--</option>
                                    {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">No of Certified warehouses (Optional)</label>
                            <div className="grid grid-cols-2 gap-2">
                                <input value={productionData.no_of_certified_ware_houses.value} onChange={(e) => setDataItemValue('no_of_certified_ware_houses', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <select value={productionData.no_of_certified_ware_houses.data_source} onChange={(e) => setDataItemSource('no_of_certified_ware_houses', e.target.value)} className="form-input-2" name="" id="">
                                    <option value="">--Select Source--</option>
                                    {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">No of Women involved in value chain (Optional)</label>

                            <div className="grid grid-cols-2 gap-2">
                                <input value={productionData.no_of_women_involved_in_value_chain.value} onChange={(e) => setDataItemValue('no_of_women_involved_in_value_chain', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <select value={productionData.no_of_women_involved_in_value_chain.data_source} onChange={(e) => setDataItemSource('no_of_women_involved_in_value_chain', e.target.value)} className="form-input-2" name="" id="">
                                    <option value="">--Select Source--</option>
                                    {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">Farm gate price for Arabica</label>
                            <div className="grid grid-cols-2 gap-2">
                                <input value={productionData.farm_gate_price_for_arabica.value} onChange={(e) => setDataItemValue('farm_gate_price_for_arabica', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <select value={productionData.farm_gate_price_for_arabica.data_source} onChange={(e) => setDataItemSource('farm_gate_price_for_arabica', e.target.value)} className="form-input-2" name="" id="">
                                    <option value="">--Select Source--</option>
                                    {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">Farm gate price for Robusta</label>

                            <div className="grid grid-cols-2 gap-2">
                                <input value={productionData.farm_gate_price_for_robusta.value} onChange={(e) => setDataItemValue('farm_gate_price_for_robusta', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <select value={productionData.farm_gate_price_for_robusta.data_source} onChange={(e) => setDataItemSource('farm_gate_price_for_robusta', e.target.value)} className="form-input-2" name="" id="">
                                    <option value="">--Select Source--</option>
                                    {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="w-2/3 mt-4">
                            <AppButton callBackFun={() => saveProductionData()} showLoader={isSavingData} spinnerClass="inline w-3 h-3 mr-2 text-prim-yellow animate-spin fill-black" className="prim-btn-2 mb-2" text="Save" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CreateProductionPage;